import type { DependencyList, ReactNode } from 'react';
import { useContext, useEffect, useRef } from 'react';
import {
  type PageSidebarSlot,
  PageContentTopContext,
  PageLayoutContext,
  PageLayoutSlotContext,
} from './context';

export const usePageLayoutSlots = () => useContext(PageLayoutSlotContext); // layout components
export const usePageLayoutActions = () => useContext(PageLayoutContext); // pages/hooks

/**
 * Distance from the viewport top to the page content area (below the page
 * header). Consumed by the right sidebar and by floating `SidePanel` overlays
 * so neither has to measure the header height itself.
 */
export const usePageContentTop = () => useContext(PageContentTopContext);

export function usePageHeaderSlot(
  render: () => ReactNode,
  deps: DependencyList,
) {
  const { setPageHeaderSlot } = usePageLayoutActions();
  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    setPageHeaderSlot(renderRef.current());
    return () => setPageHeaderSlot(null);
  }, [setPageHeaderSlot, ...deps]);
}

export function usePageSidebarSlot(
  render: () => PageSidebarSlot | null,
  deps: DependencyList,
) {
  const { setPageSidebarSlot } = usePageLayoutActions();
  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    setPageSidebarSlot(renderRef.current());
    return () => setPageSidebarSlot(null);
  }, [setPageSidebarSlot, ...deps]);
}
